import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Shield, Loader2, Users, Edit, Trash2, CheckCircle2, UserMinus, Settings2, Plus, X, Building, User } from "lucide-react";
import { toast } from "sonner";
import API from "../api/api01";

interface Group {
  id: string;
  name: string;
  description: string;
  group_type?: string;
  membersData?: any[];
  permissionsData?: any[];
}

interface PagePermission {
  module_id: string;
  page_name: string;
  view: boolean;
  insert: boolean;
  update: boolean;
  delete: boolean;
  export: boolean;
  import_: boolean;
  print_: boolean;
  approve: boolean;
  reject: boolean;
  custom: boolean;
  parent_id?: string | null;
}

const actionFields = [
  { key: "view", label: "View" },
  { key: "insert", label: "Insert" },
  { key: "update", label: "Update" },
  { key: "delete", label: "Delete" },
  { key: "export", label: "Export" },
  { key: "import_", label: "Import" },
  { key: "print_", label: "Print" },
  { key: "approve", label: "Approve" },
  { key: "reject", label: "Reject" },
  { key: "custom", label: "Custom" },
] as const;

const THEMES = [
  { header: 'bg-blue-600', textInfo: 'text-blue-600', icon: 'bg-blue-100 text-blue-600', pill: 'bg-white', bg: 'bg-blue-50 dark:bg-blue-900/10 hover:border-blue-200 dark:hover:border-blue-800' },
  { header: 'bg-green-500', textInfo: 'text-green-600', icon: 'bg-green-100 text-green-600', pill: 'bg-white', bg: 'bg-emerald-50 dark:bg-emerald-900/10 hover:border-green-200 dark:hover:border-emerald-800' },
  { header: 'bg-amber-500', textInfo: 'text-amber-600', icon: 'bg-amber-100 text-amber-600', pill: 'bg-white', bg: 'bg-amber-50 dark:bg-amber-900/10 hover:border-amber-200 dark:hover:border-amber-800' },
  { header: 'bg-purple-500', textInfo: 'text-purple-600', icon: 'bg-purple-100 text-purple-600', pill: 'bg-white', bg: 'bg-purple-50 dark:bg-purple-900/10 hover:border-purple-200 dark:hover:border-purple-800' }
];

export function GroupsPermissionsTabV2() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [modulesList, setModulesList] = useState<any[]>([]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [isMatrixOpen, setIsMatrixOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState({ name: "", description: "", group_type: "client" });
  const [isSaving, setIsSaving] = useState(false);

  const [pages, setPages] = useState<PagePermission[]>([]);
  const [savingPerms, setSavingPerms] = useState(false);

  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [userRole, setUserRole] = useState<string>("super_admin");
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.role) setUserRole(user.role.toLowerCase());
        if (user.id) setUserId(user.id);
      } catch (e) { }
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gRes, mRes] = await Promise.all([
        API.get("/superadmin/groups"),
        API.get("/superadmin/modules?flat=true").catch(() => ({ data: [] }))
      ]);

      setModulesList(Array.isArray(mRes.data) ? mRes.data : []);
      const groupsData = Array.isArray(gRes.data) ? gRes.data : [];

      const fullData = await Promise.all(groupsData.map(async (g: any) => {
        let members: any[] = [];
        let permissions: any[] = [];
        try {
          const [membRes, permRes] = await Promise.all([
            API.get(`/superadmin/groups/${g.id}/members`),
            API.get(`/superadmin/group-permissions/${g.id}`)
          ]);
          members = membRes.data;
          permissions = permRes.data;
        } catch (e) { }
        return {
          id: g.id,
          name: g.name,
          description: g.description || "",
          group_type: g.group_type || "client",
          membersData: members,
          permissionsData: permissions
        };
      }));

      setGroups(fullData);
    } catch {
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!formData.name || !formData.description) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      setIsSaving(true);
      const res = await API.post("/superadmin/groups", formData);
      const newGroupId = res.data.id;

      toast.success("Group created! Now set its permissions.");
      setIsCreateDialogOpen(false);
      setFormData({ name: "", description: "", group_type: "client" });
      
      // Refresh list
      await fetchData();

      // Find the new group in the updated state to open its matrix
      // We can't rely on the 'groups' state immediately after fetchData() due to async nature,
      // but we can construct the Group object for the matrix view.
      const newGroup: Group = {
        id: res.data.id,
        name: res.data.name,
        description: res.data.description,
        group_type: res.data.group_type,
        membersData: [],
        permissionsData: []
      };

      openMatrix(newGroup);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Error creating group");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedGroup || !formData.name || !formData.description) { toast.error("Please fill in all fields"); return; }
    try {
      setIsSaving(true);
      await API.put(`/superadmin/groups/${selectedGroup.id}`, formData);
      toast.success("Group updated successfully!");
      setIsEditDialogOpen(false);
      fetchData();
    } catch { toast.error("Failed to update group"); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedGroup) return;
    try {
      await API.delete(`/superadmin/groups/${selectedGroup.id}`);
      toast.success("Group removed successfully!");
      setDeleteDialogOpen(false);
      fetchData();
    } catch { toast.error("Failed to delete group"); }
  };

  const handleRemoveMember = async (member: any) => {
    try {
      await API.delete(`/superadmin/groups/members/${member.role}/${member.id}`);
      setSelectedGroup(prev => {
        if (!prev) return prev;
        return { ...prev, membersData: prev.membersData?.filter(m => m.id !== member.id) };
      });
      fetchData();
      toast.success(`${member.name} removed from group`);
    } catch { toast.error("Failed to remove member"); }
  };

  const fetchAvailableUsers = async () => {
    try {
      setIsFetchingUsers(true);
      const [cRes, cuRes] = await Promise.all([
        API.get("/superadmin/clients"),
        API.get("/superadmin/customers")
      ]);

      const all = [
        ...cRes.data.map((c: any) => ({ ...c, roleType: 'Client' })),
        ...cuRes.data.map((c: any) => ({ ...c, roleType: 'Customer' }))
      ];

      setAvailableUsers(all);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setIsFetchingUsers(false);
    }
  };

  const handleAddMember = async (user: any) => {
    if (!selectedGroup) return;
    try {
      const endpoint = user.roleType === 'Client' ? `/superadmin/clients/${user.id}` : `/superadmin/customers/${user.id}`;
      await API.put(endpoint, {
        group_id: selectedGroup.id
      });
      toast.success(`${user.name} added to group`);
      setIsAddMemberOpen(false);
      const membRes = await API.get(`/superadmin/groups/${selectedGroup.id}/members`);
      setSelectedGroup(prev => prev ? { ...prev, membersData: membRes.data } : prev);
      fetchData();
    } catch {
      toast.error("Failed to add member");
    }
  };

  const openMatrix = (group: Group) => {
    setSelectedGroup(group);

    const safeModulesList = Array.isArray(modulesList) ? modulesList : [];
    const defaultPages = safeModulesList.map((m: any) => ({
      module_id: m.id, page_name: m.name, parent_id: m.parent_id || null,
      view: false, insert: false, update: false, delete: false, export: false, import_: false, print_: false, approve: false, reject: false, custom: false,
    }));

    const remotePerms = group.permissionsData || [];
    const merged = remotePerms.length > 0
      ? defaultPages.map((p: any) => { const r = remotePerms.find((d: any) => d.module_id === p.module_id); return r ? { ...p, ...r } : p; })
      : defaultPages;

    setPages(merged);
    setIsMatrixOpen(true);
  };

  const handleSavePerms = async () => {
    if (!selectedGroup) return;
    try {
      setSavingPerms(true);
      await API.post("/superadmin/group-permissions/save", { group_id: selectedGroup.id, permissions: pages });
      toast.success("Permissions saved successfully.");
      setIsMatrixOpen(false);
      fetchData();
    } catch { toast.error("Failed to save permissions"); }
    finally { setSavingPerms(false); }
  };

  const togglePermission = (moduleId: string, field: keyof PagePermission) =>
    setPages((prev) => prev.map((p) => p.module_id === moduleId ? { ...p, [field]: !p[field] } : p));

  const toggleModulePerms = (moduleId: string) => {
    setPages((prev) => {
      const modulePerms = prev.find(p => p.module_id === moduleId);
      if (!modulePerms) return prev;

      const isAllChecked = actionFields.every(af => modulePerms[af.key as keyof PagePermission]);
      // If everything in this module is already ON, we turn everything OFF.
      // Otherwise (if none are on OR if only some are on), we turn everything ON.
      const newState = !isAllChecked;

      // Find children if this is a parent
      const childModuleIds = prev.filter(p => p.parent_id === moduleId).map(p => p.module_id);
      const targetIds = [moduleId, ...childModuleIds];

      return prev.map(p => {
        if (targetIds.includes(p.module_id)) {
          const updated = { ...p };
          actionFields.forEach(af => {
            (updated as any)[af.key] = newState;
          });
          return updated;
        }
        return p;
      });
    });
  };

  const toggleAllGlobal = () => {
    setPages((prev) => {
      const isEverythingChecked = prev.every(p => actionFields.every(af => p[af.key as keyof PagePermission]));
      const newState = !isEverythingChecked;
      return prev.map(p => {
        const updated = { ...p };
        actionFields.forEach(af => {
          (updated as any)[af.key] = newState;
        });
        return updated;
      });
    });
  };

  // Modern component for rendering a single module row
  const ModulePermissionRow = ({ module, isChild = false }: { module: PagePermission, isChild?: boolean }) => {
    const isAnyChecked = actionFields.some(af => module[af.key as keyof PagePermission]);
    const isAllChecked = actionFields.every(af => module[af.key as keyof PagePermission]);
    return (
      <div className={`p-4 rounded-xl border transition-all duration-300 shadow-sm ${isChild ? 'bg-muted/30 border-border/60 mt-3' : 'bg-gradient-to-r from-muted/50 to-background border-border shadow-sm'}`}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start lg:items-center gap-3 w-full lg:w-auto overflow-hidden">
            {isChild && (
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-1 lg:mt-0">
                <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-extrabold text-[14px] text-foreground truncate">
                {module.page_name}
              </h4>
              <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-widest mt-0.5 truncate">Manage Details</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center w-full lg:w-auto xl:flex-1 justify-end">

            <div className="flex w-full overflow-x-auto pb-1 mt-3 custom-scrollbar">
              <div className="flex items-center gap-2 min-w-max pr-2">
                {actionFields.map(af => {
                  const isChecked = module[af.key as keyof PagePermission] as boolean;
                  return (
                    <button
                      key={af.key}
                      onClick={() => togglePermission(module.module_id, af.key as keyof PagePermission)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-bold cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isChecked
                        ? 'bg-blue-500 border-blue-500 text-white shadow-md shadow-blue-500/20'
                        : 'bg-background border-border text-muted-foreground hover:border-slate-300 dark:hover:border-slate-700'
                        }`}
                    >
                      {!isChecked && <div className="h-2 w-2 rounded-full border border-border mr-0.5" />}
                      {isChecked && <CheckCircle2 className="h-3 w-3" />}
                      {af.label}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
          <div className="w-[1px] h-10 bg-border mx-4 hidden lg:block shrink-0" />
          <div className="shrink-0 flex items-center gap-3">
            <span className="text-[10px] uppercase font-bold text-muted-foreground hidden sm:block">Toggle All</span>
            <button
              title="Toggle All Permissions"
              onClick={(e) => { e.stopPropagation(); toggleModulePerms(module.module_id); }}
              className={`shrink-0 h-6 w-11 rounded-full flex items-center p-1 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isAnyChecked ? 'bg-blue-600' : 'bg-muted'}`}
            >
              <div className={`h-4 w-4 bg-background rounded-full shadow-md transition-transform duration-300 ${isAnyChecked ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {groups.filter(group => {
          if (userRole !== 'client') return true;
          if (group.group_type === 'client') {
            if (!userId) return false;
            return group.membersData?.some(m => m.id === userId);
          }
          return true;
        }).map((group, index) => {
          const theme = THEMES[index % THEMES.length];
          const members = group.membersData || [];
          const perms = group.permissionsData || [];
          let activePermsCount = 0;
          const pillLabels = new Set<string>();

          perms.forEach((p: any) => {
            if (p.view || p.insert || p.update || p.delete) {
              activePermsCount++;
              const modName = modulesList.find((m: any) => m.id === p.module_id)?.name || p.module_id;
              if (pillLabels.size < 6) pillLabels.add(modName);
            }
          });

          return (
            <Card key={group.id} className={`overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-800 group relative ${theme.bg}`}>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 dark:bg-white/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none" />
              <div className="p-6 pb-5 flex flex-col h-full bg-white/60 dark:bg-slate-900/60 backdrop-blur-md relative z-10">

                {/* Header */}
                <div className="flex justify-between items-start mb-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${theme.icon}`}>
                      <Shield className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                        {group.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge variant="outline" className={`py-0.5 px-2 text-[10px] font-bold uppercase tracking-widest border-current ${theme.textInfo} bg-white dark:bg-slate-900`}>
                          {group.group_type === 'customer' ? <User className="w-3 h-3 mr-1 inline" /> : <Building className="w-3 h-3 mr-1 inline" />}
                          {group.group_type}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-2 line-clamp-2">{group.description}</p>
                    </div>
                  </div>

                  {/* Actions Dropdown / Icons */}
                  {(userRole !== 'client' || group.group_type !== 'client') && (
                    <div className="flex gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setSelectedGroup(group); setFormData({ name: group.name, description: group.description, group_type: group.group_type || 'client' }); setIsEditDialogOpen(true); }} className="p-2 bg-white/80 dark:bg-slate-800/80 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 rounded-xl text-slate-400 dark:text-slate-300 transition-colors shadow-sm">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => { setSelectedGroup(group); setDeleteDialogOpen(true); }} className="p-2 bg-white/80 dark:bg-slate-800/80 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 rounded-xl text-slate-400 dark:text-slate-300 transition-colors shadow-sm">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Member Bar */}
                <div className="mt-2">
                  <div className={`w-full py-3 px-4 rounded-xl flex items-center justify-between gap-2 bg-white dark:bg-slate-900/80 border border-slate-100 dark:border-slate-800 shadow-sm`}>
                    <div className="flex items-center gap-2 font-bold text-sm text-slate-700 dark:text-slate-300">
                      <div className={`p-1.5 rounded-lg ${theme.icon} bg-opacity-20`}><Users size={16} /></div>
                      <span>{members.length} member{members.length !== 1 && 's'}</span>
                    </div>

                    <div className="flex -space-x-2 overflow-hidden">
                      {members.slice(0, 4).map((m: any, i) => (
                        <div key={i} className={`h-7 w-7 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 border-white dark:border-slate-900 ${['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-purple-500'][i % 5]}`}>
                          {(m.full_name || m.name || m.username || "U").split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                        </div>
                      ))}
                      {members.length > 4 && (
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold bg-slate-100 text-slate-600 border-2 border-white dark:border-slate-900 dark:bg-slate-800 dark:text-slate-300">
                          +{members.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Permissions section */}
                <div className="mt-6 mb-6 flex-1">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 tracking-widest uppercase">
                      Permissions Configured ({activePermsCount})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(pillLabels).slice(0, 6).map((label: string, i) => (
                      <Badge key={i} variant="outline" className={`py-1 px-2.5 gap-1.5 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-800/80 shadow-sm font-semibold rounded-lg`}>
                        {label}
                      </Badge>
                    ))}
                    {pillLabels.size > 6 && (
                      <Badge variant="outline" className={`py-1 px-2.5 border-slate-200 dark:border-slate-700 text-slate-500 bg-slate-50 dark:bg-slate-800 shadow-sm font-bold rounded-lg`}>
                        +{pillLabels.size - 6} More
                      </Badge>
                    )}
                    {pillLabels.size === 0 && (
                      <span className="text-sm font-medium text-slate-400">No permissions explicitly set.</span>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {(userRole !== 'client' || group.group_type !== 'client') ? (
                  <div className="flex gap-3 mt-auto pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                    <Button className={`flex-1 ${theme.header} hover:bg-opacity-90 text-white font-bold shadow-md shadow-${theme.header.split('-')[1]}-500/20`} onClick={() => openMatrix(group)}>
                      Access Control
                    </Button>
                    <Button variant="outline" className="flex-1 bg-white hover:bg-slate-50 font-bold text-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800 dark:text-slate-200 dark:border-slate-700 shadow-sm" onClick={() => { setSelectedGroup(group); setIsMembersDialogOpen(true); }}>
                      Members
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-3 mt-auto pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                    <div className="flex-1 text-center text-xs font-bold text-slate-400 py-2">
                      Management restricted to Super Admin
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )
        })}

        {/* Empty Add Group Card */}
        <div
          onClick={() => { setFormData({ name: "", description: "", group_type: userRole === 'client' ? "customer" : "client" }); setIsCreateDialogOpen(true) }}
          className="rounded-2xl flex flex-col items-center justify-center p-10 cursor-pointer border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-all min-h-[340px] text-slate-400 hover:text-blue-600 group">
          <div className="h-16 w-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4 text-inherit shadow-sm group-hover:scale-110 transition-transform">
            <Plus size={32} />
          </div>
          <h3 className="text-xl font-extrabold text-slate-700 dark:text-slate-300 mb-2">Create New Group</h3>
          <p className="text-sm font-medium text-center px-4">Define a new role base and customize its permissions profile.</p>
        </div>
      </div>

      {/* Dialogs */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="bg-background border-border shadow-2xl rounded-2xl overflow-hidden p-0 max-w-lg">
          <div className="p-6 bg-muted/30 border-b border-border">
            <DialogHeader><DialogTitle className="text-2xl font-black text-foreground">Create Group</DialogTitle></DialogHeader>
          </div>

          <div className="p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Group Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                {userRole !== 'client' && (
                  <div
                    onClick={() => setFormData({ ...formData, group_type: "client" })}
                    className={`cursor-pointer border rounded-xl p-4 flex items-center justify-center gap-2 font-bold transition-all ${formData.group_type === 'client' ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500/50 dark:text-blue-400' : 'bg-background border-border text-muted-foreground hover:border-slate-300 dark:hover:border-slate-700'}`}
                  >
                    <Building size={16} /> Client
                  </div>
                )}
                <div
                  onClick={() => setFormData({ ...formData, group_type: "customer" })}
                  className={`cursor-pointer border rounded-xl p-4 flex items-center justify-center gap-2 font-bold transition-all ${formData.group_type === 'customer' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-500/50 dark:text-emerald-400' : 'bg-background border-border text-muted-foreground hover:border-slate-300 dark:hover:border-slate-700'} ${userRole === 'client' ? 'col-span-2' : ''}`}
                >
                  <User size={16} /> Customer
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Group Name *</Label>
              <Input placeholder="e.g. Finance Hub" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-background border-border h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Description *</Label>
              <Input placeholder="What does this group handle?" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-background border-border h-11" />
            </div>
          </div>
          <div className="p-6 pt-0 flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setIsCreateDialogOpen(false)} className="font-bold">Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-8 rounded-xl shadow-md shadow-blue-500/20" onClick={handleCreate} disabled={isSaving}>{isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create Group</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-background border-border shadow-2xl rounded-2xl overflow-hidden p-0 max-w-lg">
          <div className="p-6 bg-muted/30 border-b border-border">
            <DialogHeader><DialogTitle className="text-2xl font-black text-foreground">Edit Group</DialogTitle></DialogHeader>
          </div>
          <div className="p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Group Type *</Label>
              <div className="grid grid-cols-2 gap-3">
                {userRole !== 'client' && (
                  <div
                    onClick={() => setFormData({ ...formData, group_type: "client" })}
                    className={`cursor-pointer border rounded-xl p-4 flex items-center justify-center gap-2 font-bold transition-all ${formData.group_type === 'client' ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-900/20 dark:border-blue-500/50 dark:text-blue-400' : 'bg-background border-border text-muted-foreground hover:border-slate-300 dark:hover:border-slate-700'}`}
                  >
                    <Building size={16} /> Client
                  </div>
                )}
                <div
                  onClick={() => setFormData({ ...formData, group_type: "customer" })}
                  className={`cursor-pointer border rounded-xl p-4 flex items-center justify-center gap-2 font-bold transition-all ${formData.group_type === 'customer' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-500/50 dark:text-emerald-400' : 'bg-background border-border text-muted-foreground hover:border-slate-300 dark:hover:border-slate-700'} ${userRole === 'client' ? 'col-span-2' : ''}`}
                >
                  <User size={16} /> Customer
                </div>
              </div>
            </div>
            <div className="space-y-2"><Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Group Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-background border-border h-11" /></div>
            <div className="space-y-2"><Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-background border-border h-11" /></div>
          </div>
          <div className="p-6 pt-0 flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setIsEditDialogOpen(false)} className="font-bold">Cancel</Button>
            <Button className="bg-blue-600 text-white hover:bg-blue-700 font-bold h-11 px-8 rounded-xl shadow-md shadow-blue-500/20" onClick={handleEdit} disabled={isSaving}>{isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Matrix Modal */}
      <Dialog open={isMatrixOpen} onOpenChange={setIsMatrixOpen}>
        <DialogContent
          className="!max-w-none !w-screen !h-screen !m-0 !rounded-none !border-none bg-background flex flex-col p-0 overflow-hidden shadow-2xl"
          style={{ top: '0', left: '0', transform: 'translate(0, 0)' }}
        >
          <div className="p-6 xl:px-10 border-b border-border flex justify-between items-center bg-background shrink-0 sticky top-0 z-10 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center border border-blue-100 dark:border-blue-900/30">
                <Settings2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-foreground leading-none">
                  Core Access Control Matrix
                </h2>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="secondary" className="font-bold text-[10px] uppercase tracking-widest bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                    Group: {selectedGroup?.name}
                  </Badge>
                  <p className="text-[13px] font-semibold text-muted-foreground">{selectedGroup?.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="hidden md:flex items-center gap-2 font-bold h-11 px-6 rounded-xl border-blue-200 dark:border-blue-900/30 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                onClick={toggleAllGlobal}
              >
                <CheckCircle2 className="w-4 h-4" />
                Toggle All Everything
              </Button>
              <Button variant="outline" size="icon" className="rounded-xl" onClick={() => setIsMatrixOpen(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1 overflow-y-auto p-6 xl:px-10 px-4 md:px-8">
            <div className="space-y-6 pb-24 w-full max-w-7xl mx-auto">
              {(() => {
                const safePages = Array.isArray(pages) ? pages : [];
                const parentModules = safePages.filter(p => !p.parent_id || !safePages.some(fp => fp.module_id === p.parent_id));
                return parentModules.map((parent) => {
                  const childModules = pages.filter(p => p.parent_id === parent.module_id);

                  return (
                    <div key={parent.module_id} className="bg-card rounded-2xl p-5 border border-border flex flex-col gap-2">
                      <div className="mb-2">
                        <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 pl-1">Parent Module Area</h3>
                        <ModulePermissionRow module={parent} />
                      </div>

                      {childModules.length > 0 && (
                        <div className="mt-2 pl-4 md:pl-8 relative before:absolute before:left-4 before:top-2 before:bottom-6 before:w-[2px] before:bg-slate-100 dark:before:bg-slate-800/60">
                          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 pl-5">Nested Hierarchy ({childModules.length})</h3>
                          <div className="space-y-2 relative z-10 pl-5">
                            {childModules.map(child => (
                              <ModulePermissionRow key={child.module_id} module={child} isChild={true} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              })()}

              {pages.length === 0 && (
                <div className="py-20 text-center">
                  <Shield className="w-16 h-16 mx-auto text-slate-200 dark:text-slate-800 mb-4" />
                  <p className="text-lg font-bold text-slate-500">No modules available to configure.</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <style>
            {`
               .custom-scrollbar::-webkit-scrollbar {
                  height: 4px;
               }
               .custom-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgba(148, 163, 184, 0.3);
                  border-radius: 10px;
               }
            `}
          </style>

          <div className="p-4 sm:p-6 border-t border-border bg-background backdrop-blur-md shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-20">
            <p className="text-[11px] sm:text-sm font-semibold text-muted-foreground leading-snug">
              Changes apply instantly upon saving. Children permissions act fully independent of parents.
            </p>
            <div className="flex justify-end shrink-0">
              <Button variant="outline" className="mr-3 font-bold h-11 px-4 sm:px-6 rounded-xl border-border" onClick={() => setIsMatrixOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-4 sm:px-8 rounded-xl shadow-md shadow-blue-500/30" onClick={handleSavePerms} disabled={savingPerms}>
                {savingPerms ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
                <span className="hidden sm:inline">Commit Permissions</span>
                <span className="inline sm:hidden">Save</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Modal */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="bg-background border-border shadow-2xl max-w-3xl overflow-hidden flex flex-col p-0 rounded-2xl">
          <DialogHeader className="p-6 border-b border-border bg-muted/30">
            <div className="flex justify-between items-center">
              <div>
                <DialogTitle className="flex items-center gap-3 text-2xl font-black">
                  <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-600 border border-indigo-100 dark:border-indigo-900/30">
                    <Users className="h-6 w-6" />
                  </div>
                  Member Directory
                </DialogTitle>
                <div className="mt-2 text-sm font-medium text-muted-foreground">Managing individuals assigned to <strong>{selectedGroup?.name}</strong></div>
              </div>
            </div>
          </DialogHeader>

          <div className="p-6 flex-1 overflow-y-auto max-h-[60vh]">
            {isAddMemberOpen ? (
              <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="icon" onClick={() => setIsAddMemberOpen(false)} className="shrink-0 h-11 w-11 rounded-xl">
                    <X className="h-5 w-5" />
                  </Button>
                  <div className="relative flex-1">
                    <Input
                      placeholder={`Search ${selectedGroup?.group_type === 'client' ? 'clients' : 'customers'} by name, email or role...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-11 pl-4 bg-background border-border focus-visible:ring-indigo-500 rounded-xl font-medium"
                      autoFocus
                    />
                  </div>
                </div>

                <div className="border border-border rounded-2xl bg-card overflow-hidden shadow-sm">
                  {isFetchingUsers ? (
                    <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-500" /></div>
                  ) : availableUsers.length === 0 ? (
                    <div className="py-20 text-center flex flex-col items-center">
                      <UserMinus className="h-12 w-12 text-muted-foreground mb-3" />
                      <div className="text-muted-foreground font-bold">No eligible candidates found.</div>
                    </div>
                  ) : (
                    <div className="divide-y divide-border h-[400px] overflow-y-auto">
                      {availableUsers
                        .filter(u => {
                          const matchesSearch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                              u.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                              u.roleType.toLowerCase().includes(searchTerm.toLowerCase());
                          
                          const groupTypeLower = selectedGroup?.group_type?.toLowerCase();
                          const userRoleLower = u.roleType?.toLowerCase();
                          
                          return matchesSearch && groupTypeLower === userRoleLower;
                        })
                        .map((user) => (
                          <div key={user.id} className="p-4 flex justify-between items-center hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 flex items-center justify-center font-bold text-base border border-indigo-200 dark:border-indigo-800/50">
                                {user.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                              </div>
                              <div>
                                <div className="text-sm font-extrabold text-slate-800 dark:text-slate-200">{user.name}</div>
                                <div className="text-[11px] text-slate-500 font-semibold mt-0.5">{user.email}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <Badge variant="outline" className={`font-bold uppercase tracking-widest text-[9px] ${user.roleType === 'Client' ? 'text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800/50' : 'text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800/50'}`}>
                                {user.roleType}
                              </Badge>
                              <Button size="sm" className="h-9 px-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold shadow-sm" onClick={() => handleAddMember(user)}>
                                Assign
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="animate-in fade-in duration-300">
                {!selectedGroup?.membersData?.length ? (
                  <div className="text-center py-20 bg-muted/20 border-2 border-dashed border-border rounded-2xl flex flex-col items-center">
                    <Users className="h-16 w-16 text-muted-foreground mb-4" />
                    <div className="text-foreground font-bold text-lg">Group is currently empty</div>
                    <div className="text-muted-foreground font-medium text-sm mt-1">Add members to grant them these permissions.</div>
                  </div>
                ) : (
                  <div className="border border-border rounded-2xl overflow-hidden shadow-sm">
                    <Table>
                      <TableHeader className="bg-muted/40 border-b border-border">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="text-[11px] font-black uppercase tracking-widest text-muted-foreground py-4 pl-6">Core Identity</TableHead>
                          <TableHead className="text-[11px] font-black uppercase tracking-widest text-muted-foreground py-4">Context Role</TableHead>
                          <TableHead className="text-right text-[11px] font-black uppercase tracking-widest text-muted-foreground py-4 pr-6">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedGroup.membersData.map((m) => (
                          <TableRow key={m.id} className="hover:bg-muted/50 transition-colors border-border/50">
                            <TableCell className="pl-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center font-bold text-sm border border-white dark:border-slate-900">
                                  {(m.full_name || m.name || m.username || "U").split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                </div>
                                <div>
                                  <div className="font-extrabold text-[14px] text-slate-800 dark:text-slate-200">{m.full_name || m.name || m.username || "Unknown User"}</div>
                                  <div className="text-[11px] text-slate-500 font-semibold">{m.email}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-4">
                              <Badge variant="secondary" className="text-[10px] font-bold uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-none px-2.5 py-1 rounded-md">
                                {m.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right pr-6 py-4">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors" onClick={() => handleRemoveMember(m)}>
                                      <UserMinus className="h-5 w-5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Revoke Access</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
          {!isAddMemberOpen && (
            <DialogFooter className="p-6 bg-muted/20 border-t border-border">
              <Button variant="outline" onClick={() => setIsMembersDialogOpen(false)} className="rounded-xl font-bold border-border h-11 px-8">Close Overview</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete group dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background border-border shadow-2xl rounded-2xl p-0 overflow-hidden">
          <div className="p-6 pb-4">
            <AlertDialogHeader>
              <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <AlertDialogTitle className="text-2xl font-black text-center text-foreground">Delete Group?</AlertDialogTitle>
              <AlertDialogDescription className="text-center font-medium mt-2">
                This will permanently delete <strong className="text-red-500">{selectedGroup?.name}</strong>. All mapped entity members will lose any permissions derived from this group instantly.
                <br /><br />This action is irreversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <div className="p-6 pt-2 flex justify-center gap-3 bg-muted/30 border-t border-border">
            <AlertDialogCancel className="font-bold border-border rounded-xl h-11 px-6 mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700 font-bold shadow-md shadow-red-500/20 rounded-xl h-11 px-6">Confirm Delete</AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
